import React from 'react';
import {Box, Divider, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper} from "@mui/material";
import {FaStepper, FaStep} from "../FactureeComponents/FaStepper";
import ShowCode from "../helpers/ShowCode.js";

const FactureeCompStepperExmpl = () => {

  const stepperExample = `<>
  {/* FaStepper with multiple steps and callbacks */}
  <FaStepper
    id={'myStepper'}
    steps={['Basic Information', 'Machining Types', 'Materials', 'Properties']}
    optionalSteps={[false, false, true, false]}
    onFinish={() => {
      console.log("Finished all steps");
    }}
    allowReset={true}
    onReset={() => {
      console.log("Stepper reset");
    }}
  >
    <FaStep step={0}>
      <Typography>Basic Information</Typography>
    </FaStep>
    <FaStep step={1}>
      <Typography>Machining Types</Typography>
    </FaStep>
    <FaStep step={2}>
      <Typography>Materials</Typography>
    </FaStep>
    <FaStep step={3}>
      <Typography>Properties</Typography>
    </FaStep>
    <FaStep step={"complete"}>
      <Typography>All steps completed - you're finished</Typography>
    </FaStep>
  </FaStepper>
</>`;

  return (
      <Box sx={{padding: '8rem 8vw 2rem 8vw'}}>
        <Typography variant={"h3"} sx={{marginBottom: '2rem'}}>FaStepper Component</Typography>
        <Typography sx={{marginBottom: '2rem', maxWidth: '600px'}}>
          The FaStepper component guides users through a multi-step process with a clear progress indicator.
          It handles navigation between steps, tracks completion status, and provides handlers for finishing
          and resetting the process. FaStepper is an extension of the MUI Stepper component.
        </Typography>

        <Divider/>

        <Typography variant={"h4"} sx={{marginBottom: '1.5rem', marginTop: '2.5rem'}}>
          Multi-step Process Example
        </Typography>

        <FaStepper
            id={'myStepper'}
            steps={['Basic Information', 'Machining Types', 'Materials', 'Properties']}
            optionalSteps={[false, false, true, false]}
            onFinish={() => {
              return alert("You are done")
            }}
            allowReset={true}
            onReset={() => {
              return alert("You are re-setting!")
            }}
        >
          <FaStep step={0}>
            <Typography>Basic Information</Typography>
          </FaStep>
          <FaStep step={1}>
            <Typography>Machining Types</Typography>
          </FaStep>
          <FaStep step={2}>
            <Typography>Materials</Typography>
          </FaStep>
          <FaStep step={3}>
            <Typography>Properties</Typography>
          </FaStep>
          <FaStep step={"complete"}>
            <Typography>All steps completed - you&apos;re finished</Typography>
          </FaStep>
        </FaStepper>

        <Divider sx={{mt: 8}}/>

        <ShowCode codeString={stepperExample} scope={{React, FaStepper, FaStep, Typography}}/>

        <Typography variant={"h4"} sx={{marginBottom: '0.5rem', marginTop: '3rem'}}>API Reference - FaStepper</Typography>
        <Typography variant={"body2"} color="text.secondary" sx={{marginBottom: '2rem'}}>
          Complete list of all props supported by the FaStepper component.
        </Typography>

        <TableContainer component={Paper} sx={{marginBottom: '2rem', '& code': {fontSize: '0.875rem'}}}>
          <Table>
            <TableHead>
              <TableRow sx={{backgroundColor: 'primary.main'}}>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '20%'}}>Prop</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '25%'}}>Type</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '15%'}}>Default</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '40%'}}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><code>steps</code> *</TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>Array&lt;string&gt;</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Array of step labels to display in the stepper.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>onFinish</code> *</TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>Function</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Callback function triggered when the user completes all steps.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>id</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Optional ID attribute for the stepper component.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>allowReset</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>boolean</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>false</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>When true, displays a reset button after completion.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>onReset</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>Function</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Callback function triggered when the reset button is clicked.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>optionalSteps</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>Array&lt;boolean&gt;</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Array indicating which steps are optional; matches the steps array by index.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>buttonTextBack</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>"Back"</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Custom text for the back button.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>buttonTextNext</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>"Next"</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Custom text for the next button.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>buttonTextFinish</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>"Finish"</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Custom text for the finish button.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>buttonTextSkip</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>"Skip"</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Custom text for the skip button (shown on optional steps).</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>buttonTextReset</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>"Reset"</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Custom text for the reset button.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>children</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>any</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>FaStep components defining the content for each step.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>sx</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>any</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Material-UI sx prop for custom styling.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant={"h4"} sx={{marginBottom: '0.5rem', marginTop: '3rem'}}>API Reference - FaStep</Typography>
        <Typography variant={"body2"} color="text.secondary" sx={{marginBottom: '2rem'}}>
          Complete list of all props supported by the FaStep component.
        </Typography>

        <TableContainer component={Paper} sx={{marginBottom: '2rem', '& code': {fontSize: '0.875rem'}}}>
          <Table>
            <TableHead>
              <TableRow sx={{backgroundColor: 'primary.main'}}>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '20%'}}>Prop</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '25%'}}>Type</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '15%'}}>Default</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '40%'}}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><code>step</code> *</TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>number | string</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Step index (0-based) or "complete" for the completion step content.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>children</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>any</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Content to display when this step is active.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>sx</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>any</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Material-UI sx prop for custom styling.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant={"body2"} color="text.secondary" sx={{fontStyle: 'italic'}}>
          * Required prop
        </Typography>
      </Box>
  )
};

export default FactureeCompStepperExmpl;
